// Considered: inline section markup in ChannelDetailOrganism — rejected because no
// sectioning construct exists anywhere in channels; a named molecule keeps the organism
// readable, is unit-testable for the intersection/ordering/omission rules, and is reusable
// for future shelf kinds. See spec Design System Reuse Audit (specs/aspect-groups.md).
// Member-count badge uses TagAtom (not ChipAtom — ChipAtom's `tone` vocabulary is
// Stream/Trajectory-specific and has no `count` prop; TagAtom is what every other
// marketplace count/facet badge in this file family already uses).
import React from 'react';
import { useTranslation } from 'localization';
import { ChannelItem, AspectGroupShelfSubgroup } from 'types/channel';
import TagAtom from '../../atoms/Tag/TagAtom';
import ItemCardMolecule from './ItemCardMolecule';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import './ChannelShelfSectionMolecule.scss';

export interface ChannelShelfSectionMoleculeProps {
  /** Group's `name` — the shelf heading. */
  name: import('types/context').LocalizedString;
  /** Group's `definition.intent` — rendered as shelf prose under the heading. */
  intent: import('types/context').LocalizedString | null;
  /** The channel's published aspect ChannelItems that belong directly to this shelf, in membership order. */
  aspectItems: ChannelItem[];
  /** Nested subgroup shelves (already server-filtered — empty ones never appear), in membership order. */
  subgroups: AspectGroupShelfSubgroup[];
  /** Resolves a subgroup's aspect `system_name`s to ChannelItems, recursively, for nested rendering. */
  resolveAspectItems: (systemNames: string[]) => ChannelItem[];
  onItemClick?: (item: ChannelItem) => void;
  onHeaderClick?: (systemName: string) => void;
  getItemSyndicationSourceName?: (item: ChannelItem) => string | undefined;
  /** Nesting depth, for the recursive sub-section class modifier. Root shelves omit this. */
  depth?: number;
  systemName?: string;
  className?: string;
}

/**
 * Does this shelf node have any aspect card that will actually render?
 *
 * The server prunes subgroups whose leaves are all unpublished, but the client narrows the
 * set again (search/facet, and the current items page), so a subtree can still resolve to
 * nothing here. The spec's omission rule is absolute — "a sub-section whose leaves all
 * vanish is omitted whole", no teasers — and it applies to a root shelf too: a caller who
 * cannot view a group gets a fail-closed empty shelf payload, which must render as nothing
 * rather than as a named heading with a `0 items` badge (that heading is itself the leak).
 */
function hasRenderableLeaves(
  aspectItems: ChannelItem[],
  subgroups: AspectGroupShelfSubgroup[],
  resolveAspectItems: (systemNames: string[]) => ChannelItem[]
): boolean {
  if (aspectItems.length > 0) return true;
  return subgroups.some((subgroup) =>
    hasRenderableLeaves(resolveAspectItems(subgroup.aspects), subgroup.subgroups, resolveAspectItems)
  );
}

const ChannelShelfSectionMolecule: React.FC<ChannelShelfSectionMoleculeProps> = ({
  name,
  intent,
  aspectItems,
  subgroups,
  resolveAspectItems,
  onItemClick,
  onHeaderClick,
  getItemSyndicationSourceName,
  depth = 0,
  systemName,
  className = '',
}) => {
  const { t } = useTranslation();

  const classes = [
    'marketplace-channel-shelf-section',
    depth > 0 ? 'marketplace-channel-shelf-section--nested' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const renderableSubgroups = subgroups.filter((subgroup) =>
    hasRenderableLeaves(resolveAspectItems(subgroup.aspects), subgroup.subgroups, resolveAspectItems)
  );

  const memberCount = aspectItems.length + renderableSubgroups.length;
  const displayName = getLocalizedText(name);
  const displayIntent = getLocalizedText(intent);
  const HeadingTag = depth > 0 ? 'h4' : 'h3';

  // Omitted whole rather than rendered as an empty named section (see hasRenderableLeaves).
  if (memberCount === 0) return null;

  return (
    <section className={classes} data-depth={depth}>
      <header className="marketplace-channel-shelf-section__header">
        {onHeaderClick && systemName ? (
          <button
            type="button"
            className="marketplace-channel-shelf-section__header-link"
            onClick={() => onHeaderClick(systemName)}
          >
            <HeadingTag className="marketplace-channel-shelf-section__name">{displayName}</HeadingTag>
          </button>
        ) : (
          <HeadingTag className="marketplace-channel-shelf-section__name">{displayName}</HeadingTag>
        )}
        <TagAtom
          tone="teal"
          label={t('marketplace.channelDetail.shelf.memberCount', { count: memberCount })}
          size="small"
          className="marketplace-channel-shelf-section__count-chip"
        />
      </header>

      {displayIntent && <p className="marketplace-channel-shelf-section__intent">{displayIntent}</p>}

      {aspectItems.length > 0 && (
        <div className="marketplace-channel-shelf-section__items-grid">
          {aspectItems.map((item) => (
            <ItemCardMolecule
              key={item.id}
              item={item}
              onClick={onItemClick}
              sourceChannelName={getItemSyndicationSourceName?.(item)}
            />
          ))}
        </div>
      )}

      {renderableSubgroups.map((subgroup) => (
        <ChannelShelfSectionMolecule
          key={subgroup.systemName}
          systemName={subgroup.systemName}
          name={subgroup.name}
          intent={subgroup.intent}
          aspectItems={resolveAspectItems(subgroup.aspects)}
          subgroups={subgroup.subgroups}
          resolveAspectItems={resolveAspectItems}
          onItemClick={onItemClick}
          onHeaderClick={onHeaderClick}
          getItemSyndicationSourceName={getItemSyndicationSourceName}
          depth={depth + 1}
          className="marketplace-channel-shelf-section__subsection"
        />
      ))}
    </section>
  );
};

export default ChannelShelfSectionMolecule;
